/**
 * Diagnostic Test Suite - Network performance diagnostics
 *
 * Four complementary tests to identify specific network issues:
 * - Test A: Single file, single connection (baseline)
 * - Test B: Parallel downloads (detect connection limits)
 * - Test C: Small files burst (detect inspection/TLS overhead)
 * - Test D: Sustained transfer (detect throttling)
 */

// ============================================================================
// Debug Configuration
// ============================================================================

/**
 * Enable verbose debug logging. Set to true during development.
 * In production, only essential logs (errors, test completion) are shown.
 */
const DEBUG = import.meta.env?.DEV ?? false;

/** Log debug messages only when DEBUG is enabled */
const debugLog = (...args: unknown[]): void => {
  if (DEBUG) {
    console.debug(...args);
  }
};

// ============================================================================
// Type Definitions for DSRecorder (Web Clone Proxy)
// ============================================================================

/**
 * Type definitions for DSRecorder (web clone proxy system)
 * DSRecorder exists in both web clone and desktop environments.
 * In web clone: URLs are encoded to route through proxy
 * In desktop: DSRecorder exists but URLs are NOT proxied (window.dsDesktop check)
 */
declare global {
  interface Window {
    DSRecorder?: {
      replaceToProxy: (url: string) => string;
      replaceProxyToOriginal: (url: string) => string;
      replaceHostnameProxyToOriginal: (url: string) => string;
      settings: {
        baseProxyDomain: string;
        sessionId: string;
      };
    };
    dsDesktop?: unknown;
  }
}

// ============================================================================
// Types
// ============================================================================

/**
 * Protocol detected via PerformanceResourceTiming.nextHopProtocol
 * Common values: 'h2' (HTTP/2), 'h3' (HTTP/3), 'http/1.1', '' (unknown)
 */
export type HttpProtocol = 'h2' | 'h3' | 'http/1.1' | 'unknown';

/**
 * Confidence level for connection forcing assessment
 * - high: Domain sharding active, multiple hostnames used, h1.1 or verified separate connections
 * - medium: Domain sharding active but h2/h3 detected (may still be multiplexed across hosts)
 * - low: Single hostname used, or protocol unknown
 */
export type ConnectionForcingConfidence = 'high' | 'medium' | 'low';

export interface ResourceTimingMetrics {
  transactionId: string; // Unique identifier for each transaction (UUID)
  url: string;
  requestUrl: string; // Full URL that was requested
  fileSize: number;
  dnsTime: number;
  tcpConnectTime: number;
  tlsHandshakeTime: number;
  ttfb: number;
  downloadDuration: number;
  totalDuration: number;
  throughputMbps: number;

  // Protocol detection (HTTP/2 and HTTP/3 awareness)
  nextHopProtocol: HttpProtocol;
  hostname: string; // Which shard hostname was used

  // Cache detection (values from Resource Timing)
  transferSize: number; // 0 if served from cache
  encodedBodySize: number; // Compressed size
  decodedBodySize: number; // Uncompressed size
  servedFromCache: boolean; // Heuristic: transferSize === 0 && decodedBodySize > 0
}

export interface TestResult {
  testId: 'A' | 'B' | 'C' | 'D';
  testName: string;
  description: string;
  startTime: number;
  endTime: number;
  totalDurationMs: number;
  totalBytes: number;
  /**
   * Goodput (overall): totalBytes * 8 / totalDurationMs / 1e6
   * This is the headline throughput metric for each test.
   * It must mathematically align with totalBytes and totalDurationMs.
   */
  goodputOverallMbps: number;
  fileCount: number;
  /**
   * Number of download requests that failed (null returned from downloadWithTiming).
   * Non-zero values indicate network issues, timeouts, or server errors.
   */
  failedCount: number;
  concurrency: number;
  metrics: ResourceTimingMetrics[];
  aggregated: AggregatedMetrics;
}

/**
 * Protocol distribution across all requests in a test
 */
export interface ProtocolAggregation {
  h2Count: number;
  h3Count: number;
  http11Count: number;
  unknownCount: number;
  totalCount: number;
  dominantProtocol: HttpProtocol;
  isMultiplexed: boolean; // true if dominant is h2 or h3
}

/**
 * Hostname distribution for sharding analysis
 */
export interface HostnameAggregation {
  hostnameCount: number; // Number of unique hostnames used
  requestsPerHostname: Record<string, number>; // hostname -> request count
  evenlyDistributed: boolean; // true if requests roughly even across hosts
}

export interface AggregatedMetrics {
  // Connection timing observability
  // These track what % of requests had measurable (non-zero) timings
  // <20% observable = "not observable / reused", ≥20% = show median + "(X% observable)"
  dnsObservablePercent: number;
  tcpObservablePercent: number;
  tlsObservablePercent: number;

  // Connection timings (median of observable requests, or 0 if <20% observable)
  medianDnsTime: number;
  medianTcpConnectTime: number;
  medianTlsHandshakeTime: number;

  // TTFB - primary latency signal
  // p10 = fast requests, p50 = typical, p95 = tail latency
  p10Ttfb: number;
  p50Ttfb: number;
  p95Ttfb: number;
  minTtfb: number;
  maxTtfb: number;

  // Download duration
  avgDownloadDuration: number;

  // Per-file throughput distribution
  // p10 = slow path diagnostic (10% of files were this slow or slower)
  // p50 = typical per-file speed
  p10ThroughputMbps: number;
  p50ThroughputMbps: number;
  minThroughputMbps: number;
  maxThroughputMbps: number;

  // Goodput (overall): totalBytesTransferred * 8 / totalElapsedWallClockSeconds / 1e6
  // This is THE headline metric - mathematically consistent with bytes and duration shown
  goodputOverallMbps: number;

  // Protocol and hostname distribution
  protocolAggregation: ProtocolAggregation;
  hostnameAggregation: HostnameAggregation;

  // Cache indicators
  cachedRequestCount: number;
  uncachedRequestCount: number;
}

export interface DeviceInfo {
  userAgent: string;
  platform: string;
  language: string;
  languages: string[];
  timezone: string;
  timezoneOffset: number;
  screenWidth: number;
  screenHeight: number;
  devicePixelRatio: number;
  hardwareConcurrency: number | null;
  deviceMemory: number | null;
  connection: ConnectionInfo | null;
}

export interface NetworkEnvironment {
  captivePortalDetected: boolean;
  publicNetworkLikely: boolean;
  indicators: string[];
}

export interface ConnectionInfo {
  effectiveType: string; // 'slow-2g', '2g', '3g', '4g'
  downlink: number; // Mbps estimate
  rtt: number; // Round-trip time estimate (ms)
  saveData: boolean;
}

export interface DiagnosticSuiteResult {
  deviceInfo: DeviceInfo;
  networkEnvironment: NetworkEnvironment;
  testA: TestResult;
  testB: TestResult;
  testC: TestResult;
  testD: TestResult;
  analysis: DiagnosticAnalysis;
  timestamp: string;
}

/**
 * Detailed analysis of connection limit detection
 */
export interface ConnectionLimitAnalysis {
  detected: boolean;
  confidence: ConnectionForcingConfidence;
  baselineGoodputMbps: number; // Test A goodput
  parallelGoodputMbps: number; // Test B goodput
  goodputRatio: number; // B/A ratio
  hostnamesUsed: number;
  dominantProtocol: HttpProtocol;
  reason: string; // Human-readable explanation
}

/**
 * Detailed analysis of inspection/processing overhead
 */
export interface InspectionOverheadAnalysis {
  detected: boolean;
  confidence: ConnectionForcingConfidence;
  baselineTtfbP50: number;
  parallelTtfbP50: number;
  smallFileTtfbP50: number;
  smallFileTtfbP95: number;
  ttfbTailLatencyRatio: number; // p95/p50 for small files
  reason: string;
}

/**
 * Detailed analysis of throttling with duration-aware windowing
 *
 * Uses configurable windows (default: min(10s, 25% of duration)) for:
 * - rampUpThroughputMbps: throughput during initial ramp-up period
 * - steadyStateThroughputMbps: throughput during final steady-state period
 * - stableWindowPct: percentage of test duration considered stable
 */
export interface ThrottlingAnalysis {
  detected: boolean;
  confidence: ConnectionForcingConfidence;
  // Duration-aware windowing metrics
  rampUpThroughputMbps: number;
  steadyStateThroughputMbps: number;
  rampUpWindowMs: number;
  steadyStateWindowMs: number;
  stableWindowPct: number;
  // Legacy fields (for backward compatibility)
  firstHalfThroughputMbps: number;
  secondHalfThroughputMbps: number;
  degradationRatio: number;
  reason: string;
}

export interface DiagnosticAnalysis {
  // Structured analysis results with confidence levels
  connectionLimit: ConnectionLimitAnalysis;
  inspectionOverhead: InspectionOverheadAnalysis;
  throttling: ThrottlingAnalysis;

  // Legacy boolean flags (for backward compatibility)
  connectionLimitDetected: boolean;
  inspectionOverheadDetected: boolean;
  throttlingDetected: boolean;

  // Summary
  issues: string[];
  recommendations: string[];

  // Overall confidence in results
  overallConfidence: ConnectionForcingConfidence;
  protocolSummary: string; // e.g., "HTTP/2 detected (multiplexed)"
}

export interface DiagnosticTestConfig {
  baseUrl: string;
  onProgress?: (testId: string, progress: number, message: string) => void;
  onTestComplete?: (result: TestResult) => void;

  /**
   * Domain sharding configuration for Test B (and optionally D).
   * Under HTTP/2 and HTTP/3, browsers multiplex many streams over a single connection.
   * Using different hostnames forces the browser to open separate connections per host,
   * making "parallel connections" actually represent real TCP connections.
   *
   * Example: ['a.fast.demo.link', 'b.fast.demo.link', 'c.fast.demo.link', ...]
   * All should point to the same edge/CDN and serve identical content.
   *
   * If not provided, falls back to single hostname (baseUrl) with lower confidence.
   */
  shardHostnames?: string[];

  /**
   * Whether to use sharding for Test D (sustained transfer).
   * Default: true
   */
  useShardingForTestD?: boolean;
}

// ============================================================================
// Asset Lists
// ============================================================================

// Asset IDs - stable URLs mapped by the /asset/ worker function
// Format: /asset/{id}/{instance} where instance is optional for multiple downloads
const SMALL_FILES = [
  'icon',
  'thumb-1',
  'thumb-2',
  'print-css',
  'main-css-gz',
  'hero-small',
  'inter-regular',
  'inter-bold',
  'inter-italic',
  'roboto-mono',
  'analytics',
  'bundle',
  'main-css',
];

const MEDIUM_FILES = [
  '10mb',
  'hero-medium',
  'hero-large',
  'gallery-1',
  'gallery-2',
];

// Large files for future use (100mb used directly in Test B)
const _LARGE_FILES = ['25mb', '50mb', '100mb'];

// ============================================================================
// Test Configuration
// ============================================================================

/**
 * Default test parameters - extracted for maintainability and potential customization.
 * These values are optimized for accurate network diagnostics while balancing test duration.
 */
const TEST_CONFIG = {
  /** Timing configuration */
  timing: {
    /** Delay between tests to let HTTP/2 connections settle (ms) */
    interTestDelayMs: 2000,
    /** Extra delay multiplier after Test B (has 100MB file) before Test C */
    postTestBDelayMultiplier: 1.5,
  },

  /** Warm-up configuration */
  warmup: {
    /** Number of warm-up requests before Test A to prime connections */
    requestCount: 3,
  },

  /** Domain sharding configuration */
  sharding: {
    /** Number of shard hostnames for connection forcing */
    defaultShardCount: 6,
    /** Shard prefixes for hostname generation */
    shardPrefixes: ['a', 'b', 'c', 'd', 'e', 'f'],
  },

  /** Test A: Single Connection Baseline */
  testA: {
    /** Number of file repeat cycles (each cycle = MEDIUM_FILES.length files) */
    repeatCycles: 8,
    /** Concurrency (always 1 for baseline) */
    concurrency: 1,
  },

  /** Test B: Parallel Downloads */
  testB: {
    /** Number of medium file repeat cycles */
    repeatCycles: 11,
    /** Number of concurrent connections */
    concurrency: 6,
    /** Large file to include (tests buffering with big downloads) */
    largeFile: '100mb',
    /** Additional medium files to reach target count */
    additionalFiles: ['10mb', 'hero-medium', 'hero-large', 'gallery-1'],
  },

  /** Test C: Small Files Burst */
  testC: {
    /** Number of files per wave */
    filesPerWave: 15,
    /** Number of waves */
    waveCount: 5,
    /** Interval between waves (ms) */
    waveIntervalMs: 2000,
  },

  /** Test D: Sustained Transfer */
  testD: {
    /** Test duration (ms) */
    durationMs: 30000,
    /** Number of concurrent connections */
    concurrency: 4,
  },

  /** Analysis thresholds */
  analysis: {
    /** Connection limit detection: parallel/baseline ratio threshold */
    connectionLimitThreshold: 0.7,
    /** Connection limit detection: threshold for low confidence (multiplexed) */
    connectionLimitThresholdLowConfidence: 0.5,
    /** Observability threshold: minimum % of requests with non-zero timing */
    observabilityThreshold: 0.2,
    /** TTFB significant increase ratio (Test C vs parallel) */
    ttfbSignificantIncreaseRatio: 1.5,
    /** TTFB high tail latency ratio (p95/p50) */
    ttfbHighTailLatencyRatio: 2.5,
    /** TTFB extreme increase ratio (Test C vs baseline) */
    ttfbExtremeIncreaseRatio: 3.0,
    /** TTFB very high tail latency ratio */
    ttfbVeryHighTailLatencyRatio: 10,
    /** TTFB extreme p95 threshold (ms) */
    ttfbExtremeP95ThresholdMs: 5000,
    /** Throttling detection: degradation ratio threshold */
    throttlingDegradationThreshold: 0.7,
    /** Throttling analysis: window cap (ms) */
    throttlingWindowCapMs: 10000,
    /** Throttling analysis: window percentage of total duration */
    throttlingWindowPercent: 0.25,
  },

  /** Logging configuration */
  logging: {
    /** Log download progress every N megabytes */
    progressLogIntervalMB: 10,
  },
} as const;

export class DiagnosticTest {
  private config: DiagnosticTestConfig;
  private abortController: AbortController | null = null;
  private isRunning = false;
  private assetInstanceCounter = 0; // Counter for stable asset URLs
  private shardHostnames: string[];
  private shardIndex = 0; // Round-robin counter for hostname distribution

  constructor(config: DiagnosticTestConfig) {
    // Validate baseUrl is a valid URL
    if (!config.baseUrl) {
      throw new Error(
        'DiagnosticTest: baseUrl is required. Use window.location.origin for same-origin requests.'
      );
    }
    try {
      new URL(config.baseUrl);
    } catch {
      throw new Error(
        `DiagnosticTest: Invalid baseUrl "${config.baseUrl}". Must be a valid URL (e.g., https://fast.demo.link).`
      );
    }

    this.config = config;

    // Initialize shard hostnames
    // If not provided, generate from baseUrl pattern (a.host, b.host, etc.)
    // or fall back to single hostname with lower confidence
    this.shardHostnames = this.initializeShardHostnames();

    // Log proxy mode status for diagnostics
    debugLog('[DiagnosticTest] Proxy mode:', this.isProxyMode());
  }

  /**
   * Initialize shard hostnames from config or generate default pattern
   */
  private initializeShardHostnames(): string[] {
    if (this.config.shardHostnames && this.config.shardHostnames.length > 0) {
      return this.config.shardHostnames;
    }

    // Try to generate shard hostnames from baseUrl
    // e.g., https://fast.demo.link -> [https://a.fast.demo.link, https://b.fast.demo.link, ...]
    try {
      const url = new URL(this.config.baseUrl);
      let hostname = url.hostname;

      // In web clone proxy mode, baseUrl is already encoded
      // Decode it to get the original hostname for domain checking
      if (this.isProxyMode()) {
        const win = window as Window & {
          DSRecorder: {
            replaceProxyToOriginal: (url: string) => string;
          };
        };
        const originalUrl = win.DSRecorder.replaceProxyToOriginal(
          this.config.baseUrl
        );
        hostname = new URL(originalUrl).hostname;
        debugLog(
          `[DiagnosticTest] Decoded proxy hostname: ${url.hostname} -> ${hostname}`
        );
      }

      // Only generate shards for production domain (fast.demo.link)
      // Skip for: localhost, preview URLs (*.pages.dev), and other environments
      const isProductionDomain = hostname === 'fast.demo.link';
      const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
      const isPreviewDomain = hostname.includes('.pages.dev');

      if (!isProductionDomain || isLocalhost || isPreviewDomain) {
        debugLog(
          `[DiagnosticTest] Skipping shard generation for ${hostname} (production=${isProductionDomain}, localhost=${isLocalhost}, preview=${isPreviewDomain})`
        );
        return [this.config.baseUrl];
      }

      // Generate shard hostnames for production using the original (decoded) hostname
      // These will be converted to proxy format automatically in getAssetUrl() when needed
      const shards: string[] = [];
      const { shardPrefixes, defaultShardCount } = TEST_CONFIG.sharding;
      for (
        let shardPrefixIndex = 0;
        shardPrefixIndex < defaultShardCount;
        shardPrefixIndex++
      ) {
        const prefix = shardPrefixes[shardPrefixIndex];
        const shardedHostname = `${prefix}.${hostname}`;
        shards.push(`${url.protocol}//${shardedHostname}`);
      }
      debugLog('[DiagnosticTest] Generated shard hostnames:', shards);
      return shards;
    } catch (error) {
      console.warn(
        '[DiagnosticTest] Could not parse baseUrl for sharding:',
        error
      );
    }

    // Fall back to single hostname
    debugLog('[DiagnosticTest] Using single hostname (no sharding)');
    return [this.config.baseUrl];
  }

  /**
   * Check if running in web clone proxy mode
   * Returns true if DSRecorder exists AND not in desktop app
   *
   * In web clone: URLs must be encoded via replaceToProxy to route through gateway
   * In desktop: DSRecorder exists but URLs should NOT be proxied
   */
  private isProxyMode(): boolean {
    const win = window as Window & {
      DSRecorder?: {
        replaceToProxy: (url: string) => string;
        replaceProxyToOriginal: (url: string) => string;
      };
      dsDesktop?: unknown;
    };

    return !!win.DSRecorder && !win.dsDesktop;
  }

  /**
   * Get a URL for a request, optionally using domain sharding.
   * Distributes requests evenly across available shard hostnames.
   *
   * In web clone proxy mode, URLs are automatically converted using DSRecorder.replaceToProxy
   * to route requests through the proxy gateway. The original hostname is preserved for metrics.
   *
   * @param assetId - The asset ID to fetch
   * @param useSharding - Whether to use domain sharding (for Test B/D)
   * @returns Object with full URL and hostname used
   */
  private getAssetUrl(
    assetId: string,
    useSharding: boolean
  ): { url: string; hostname: string } {
    const instanceId = String(this.assetInstanceCounter++).padStart(3, '0');

    let url: string;
    let hostname: string;

    if (useSharding && this.shardHostnames.length > 1) {
      // Round-robin across shard hostnames
      const shardBaseUrl =
        this.shardHostnames[this.shardIndex % this.shardHostnames.length];
      this.shardIndex++;
      hostname = new URL(shardBaseUrl).hostname;
      url = `${shardBaseUrl}/asset/${assetId}/${instanceId}`;
    } else {
      // Single hostname
      hostname = new URL(this.config.baseUrl).hostname;
      url = `${this.config.baseUrl}/asset/${assetId}/${instanceId}`;
    }

    // Convert to proxy URL if in web clone proxy mode
    // Note: hostname remains the original domain for accurate metrics tracking
    if (this.isProxyMode()) {
      const win = window as Window & {
        DSRecorder: {
          replaceToProxy: (url: string) => string;
        };
      };
      url = win.DSRecorder.replaceToProxy(url);
    }

    return { url, hostname };
  }

  /**
   * Helper to wait for a specified duration
   */
  private async delay(ms: number): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Warm up connections before starting tests
   * Fetches small files to prime HTTP/2 connection pool
   */
  private async warmupConnections(): Promise<void> {
    debugLog('[DiagnosticTest] Warming up connections...');
    const warmupPromises = [];
    const { requestCount } = TEST_CONFIG.warmup;
    for (let warmupIndex = 0; warmupIndex < requestCount; warmupIndex++) {
      const warmupFile = SMALL_FILES[warmupIndex % SMALL_FILES.length];
      warmupPromises.push(this.downloadWithTiming(warmupFile));
    }
    await Promise.all(warmupPromises);
    debugLog('[DiagnosticTest] Connection warm-up complete');
  }

  /**
   * Run all diagnostic tests
   */
  async runAll(): Promise<DiagnosticSuiteResult> {
    this.abortController = new AbortController();
    this.isRunning = true;
    this.assetInstanceCounter = 0; // Reset counter for stable URLs

    const deviceInfo = this.collectDeviceInfo();
    const networkEnvironment = await this.detectNetworkEnvironment();

    const { interTestDelayMs, postTestBDelayMultiplier } = TEST_CONFIG.timing;

    // Warm up connections before starting tests
    await this.warmupConnections();
    await this.delay(interTestDelayMs);

    const testA = await this.runTestA();
    await this.delay(interTestDelayMs);

    const testB = await this.runTestB();
    // Extra delay after Test B (has 100MB file) before Test C (small files)
    await this.delay(interTestDelayMs * postTestBDelayMultiplier);

    const testC = await this.runTestC();
    await this.delay(interTestDelayMs);

    const testD = await this.runTestD();

    const analysis = this.analyzeResults(testA, testB, testC, testD);

    this.isRunning = false;
    return {
      deviceInfo,
      networkEnvironment,
      testA,
      testB,
      testC,
      testD,
      analysis,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Collect device and network information (no permissions required)
   */
  private collectDeviceInfo(): DeviceInfo {
    // Get Network Information API data if available
    let connection: ConnectionInfo | null = null;
    const nav = navigator as Navigator & {
      connection?: {
        effectiveType?: string;
        downlink?: number;
        rtt?: number;
        saveData?: boolean;
      };
      deviceMemory?: number;
    };

    if (nav.connection) {
      connection = {
        effectiveType: nav.connection.effectiveType || 'unknown',
        downlink: nav.connection.downlink || 0,
        rtt: nav.connection.rtt || 0,
        saveData: nav.connection.saveData || false,
      };
    }

    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      languages: [...navigator.languages],
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),
      screenWidth: screen.width,
      screenHeight: screen.height,
      devicePixelRatio: window.devicePixelRatio || 1,
      hardwareConcurrency: navigator.hardwareConcurrency || null,
      deviceMemory: nav.deviceMemory || null,
      connection,
    };
  }

  /**
   * Detect network environment characteristics
   * Checks for captive portals and public network indicators
   */
  private async detectNetworkEnvironment(): Promise<NetworkEnvironment> {
    const indicators: string[] = [];
    let captivePortalDetected = false;

    // Test 1: Captive portal detection
    // Try to fetch a known endpoint and verify the response
    try {
      const testUrl = `${this.config.baseUrl}/manifest.json?portal-check=${Date.now()}`;
      const response = await fetch(testUrl, {
        cache: 'no-store',
        signal: AbortSignal.timeout(5000),
      });

      // Check if we got redirected (captive portal typically redirects)
      if (response.redirected) {
        captivePortalDetected = true;
        indicators.push('Request was redirected (captive portal likely)');
      }

      // Check if response is valid JSON (our expected response)
      const contentType = response.headers.get('content-type');
      if (contentType && !contentType.includes('application/json')) {
        captivePortalDetected = true;
        indicators.push('Unexpected content type (may be login page)');
      }

      // Check response size - captive portals often return HTML login pages
      const text = await response.text();
      if (text.includes('<html') || text.includes('<!DOCTYPE')) {
        if (!text.includes('"version"')) {
          captivePortalDetected = true;
          indicators.push(
            'HTML response instead of JSON (login page detected)'
          );
        }
      }
    } catch (error) {
      // Network error could indicate captive portal blocking
      if (error instanceof Error && error.name !== 'AbortError') {
        indicators.push('Network request failed (possible captive portal)');
      }
    }

    // Test 2: Check Network Information API hints
    const nav = navigator as Navigator & {
      connection?: {
        effectiveType?: string;
        downlink?: number;
        rtt?: number;
      };
    };

    if (nav.connection) {
      // High RTT can indicate shared/public network
      if (nav.connection.rtt && nav.connection.rtt > 200) {
        indicators.push(`High latency detected (${nav.connection.rtt}ms RTT)`);
      }

      // Slow effective type
      if (
        nav.connection.effectiveType === 'slow-2g' ||
        nav.connection.effectiveType === '2g'
      ) {
        indicators.push(
          `Slow connection type: ${nav.connection.effectiveType}`
        );
      }
    }

    // Determine if public network is likely
    const publicNetworkLikely = captivePortalDetected || indicators.length >= 2;

    return {
      captivePortalDetected,
      publicNetworkLikely,
      indicators,
    };
  }

  /**
   * Test A: Single file, single connection (baseline)
   * Downloads 40 files one-by-one to establish raw bandwidth baseline
   */
  async runTestA(): Promise<TestResult> {
    this.config.onProgress?.('A', 0, 'Starting baseline test...');

    const { repeatCycles, concurrency } = TEST_CONFIG.testA;

    // Build files: mix of medium files for variety
    const files: string[] = [];
    for (let repeatIndex = 0; repeatIndex < repeatCycles; repeatIndex++) {
      files.push(...MEDIUM_FILES);
    }

    const metrics: ResourceTimingMetrics[] = [];
    const startTime = performance.now();
    let totalBytes = 0;
    let failedCount = 0;

    for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
      if (!this.isRunning) break;

      const file = files[fileIndex];

      // Report progress based on files completed (before starting this one)
      const progress = (fileIndex / files.length) * 100;
      this.config.onProgress?.(
        'A',
        progress,
        `Downloading file ${fileIndex + 1}/${files.length}...`
      );

      const metric = await this.downloadWithTiming(file);
      if (metric) {
        metrics.push(metric);
        totalBytes += metric.fileSize;
      } else {
        failedCount++;
      }
    }

    // Final progress update
    this.config.onProgress?.(
      'A',
      100,
      `Downloaded ${files.length}/${files.length} files`
    );

    const endTime = performance.now();
    const result = this.buildTestResult(
      'A',
      'Single Connection Baseline',
      `${files.length} sequential downloads, one connection at a time`,
      startTime,
      endTime,
      totalBytes,
      concurrency,
      metrics,
      failedCount
    );

    this.config.onTestComplete?.(result);
    return result;
  }

  /**
   * Test B: Parallel downloads with domain sharding (6 connections)
   * Detects proxy/VPN connection limits and buffering.
   *
   * CRITICAL: Under HTTP/2 and HTTP/3, "parallel downloads" are multiplexed over
   * a single TCP connection. Domain sharding forces the browser to open separate
   * connections per hostname, making this test actually measure connection limits.
   *
   * Downloads 60 files in batches of 6 concurrent connections, distributed evenly
   * across shard hostnames.
   */
  async runTestB(): Promise<TestResult> {
    debugLog('[TestB] Starting parallel download test with domain sharding');
    debugLog('[TestB] Shard hostnames available:', this.shardHostnames.length);
    this.config.onProgress?.('B', 0, 'Starting parallel download test...');

    const { repeatCycles, concurrency, largeFile, additionalFiles } =
      TEST_CONFIG.testB;

    // Build files: medium files + 1 large file + additional files
    const files: string[] = [];
    for (let repeatIndex = 0; repeatIndex < repeatCycles; repeatIndex++) {
      files.push(...MEDIUM_FILES);
    }
    files.push(largeFile); // 1 large file for buffering test
    files.push(...additionalFiles);
    debugLog('[TestB] File list built:', files.length, 'files');

    const metrics: ResourceTimingMetrics[] = [];
    const startTime = performance.now();
    let totalBytes = 0;
    let failedCount = 0;

    // Download a single file WITH SHARDING (no progress update here - done at batch level)
    const downloadFile = async (file: string, index: number): Promise<void> => {
      if (!this.isRunning) {
        debugLog('[TestB] Skipping download - not running');
        return;
      }
      debugLog(`[TestB] Starting download ${index}: ${file}`);
      const downloadStart = performance.now();
      // Use sharding = true to distribute across hostnames
      const metric = await this.downloadWithTiming(file, true);
      const downloadDuration = performance.now() - downloadStart;
      debugLog(
        `[TestB] Completed download ${index}: ${file} in ${downloadDuration.toFixed(0)}ms`,
        metric
          ? `(${(metric.fileSize / 1024 / 1024).toFixed(1)}MB from ${metric.hostname}, protocol: ${metric.nextHopProtocol})`
          : '(failed)'
      );
      if (metric) {
        metrics.push(metric);
        totalBytes += metric.fileSize;
      } else {
        failedCount++;
      }
    };

    // Process in batches of `concurrency`, report progress after each batch completes
    const totalBatches = Math.ceil(files.length / concurrency);
    debugLog('[TestB] Total batches:', totalBatches);
    let completedFilesCount = 0;

    for (let batchNum = 0; batchNum < totalBatches; batchNum++) {
      if (!this.isRunning) {
        debugLog('[TestB] Stopping - not running');
        break;
      }

      const batchStart = batchNum * concurrency;
      const batch = files.slice(batchStart, batchStart + concurrency);

      debugLog(
        `[TestB] Starting batch ${batchNum + 1}/${totalBatches}:`,
        batch
      );

      const batchStartTime = performance.now();
      await Promise.all(
        batch.map((file, idx) => downloadFile(file, batchStart + idx))
      );
      const batchDuration = performance.now() - batchStartTime;

      // Update completed count and report progress AFTER batch completes
      completedFilesCount += batch.length;
      const progressPercent = (completedFilesCount / files.length) * 100;

      debugLog(
        `[TestB] Batch ${batchNum + 1} completed in ${batchDuration.toFixed(0)}ms`
      );
      this.config.onProgress?.(
        'B',
        progressPercent,
        `Downloaded ${completedFilesCount}/${files.length} files`
      );
    }

    // Final progress update
    this.config.onProgress?.(
      'B',
      100,
      `Downloaded ${files.length}/${files.length} files`
    );

    const endTime = performance.now();
    const result = this.buildTestResult(
      'B',
      'Parallel Downloads',
      `60 files with 6 concurrent connections across ${this.shardHostnames.length} hostnames`,
      startTime,
      endTime,
      totalBytes,
      concurrency,
      metrics,
      failedCount
    );

    this.config.onTestComplete?.(result);
    return result;
  }

  /**
   * Test C: Many small files in waves (75 files total)
   * Detects inspection overhead, TLS overhead, per-request latency
   * Downloads 15 files every 2 seconds × 5 waves = 75 files
   */
  async runTestC(): Promise<TestResult> {
    this.config.onProgress?.('C', 0, 'Starting small files burst test...');

    const { filesPerWave, waveCount, waveIntervalMs } = TEST_CONFIG.testC;
    const totalFiles = filesPerWave * waveCount;

    // Build file list for each wave (repeat small files to fill wave)
    const buildWaveFiles = (): string[] => {
      const waveFiles: string[] = [];
      while (waveFiles.length < filesPerWave) {
        for (const file of SMALL_FILES) {
          waveFiles.push(file);
          if (waveFiles.length >= filesPerWave) break;
        }
      }
      return waveFiles;
    };

    const metrics: ResourceTimingMetrics[] = [];
    const startTime = performance.now();
    let totalBytes = 0;
    let failedCount = 0;

    // Download a single file (no progress update here - done at wave level)
    const downloadFile = async (file: string): Promise<void> => {
      if (!this.isRunning) return;
      const metric = await this.downloadWithTiming(file);
      if (metric) {
        metrics.push(metric);
        totalBytes += metric.fileSize;
      } else {
        failedCount++;
      }
    };

    // Download in waves: 30 files every 2 seconds × 10 waves
    for (let waveIndex = 0; waveIndex < waveCount; waveIndex++) {
      if (!this.isRunning) break;

      const waveFiles = buildWaveFiles();
      const completedFiles = waveIndex * filesPerWave;

      // Report progress at start of wave
      this.config.onProgress?.(
        'C',
        (waveIndex / waveCount) * 100,
        `Wave ${waveIndex + 1}/${waveCount}: downloading ${filesPerWave} files (${completedFiles}/${totalFiles})`
      );

      // Download all files in this wave concurrently
      await Promise.all(waveFiles.map(downloadFile));

      // Wait before next wave (except for last wave)
      if (waveIndex < waveCount - 1 && this.isRunning) {
        await new Promise((resolve) => setTimeout(resolve, waveIntervalMs));
      }
    }

    // Final progress update
    this.config.onProgress?.(
      'C',
      100,
      `Downloaded ${totalFiles}/${totalFiles} small files`
    );

    const endTime = performance.now();
    const result = this.buildTestResult(
      'C',
      'Small Files Burst',
      `${totalFiles} small files (15 every 2s × 5 waves)`,
      startTime,
      endTime,
      totalBytes,
      filesPerWave,
      metrics,
      failedCount
    );

    this.config.onTestComplete?.(result);
    return result;
  }

  /**
   * Test D: Sustained transfer (30 seconds)
   * Detects traffic shaping and throttling.
   *
   * Optionally uses domain sharding (enabled by default via config.useShardingForTestD).
   * Sharding helps ensure consistent measurement under HTTP/2 and HTTP/3.
   */
  async runTestD(): Promise<TestResult> {
    const useSharding = this.config.useShardingForTestD !== false;
    debugLog(
      '[TestD] Starting sustained transfer test, sharding:',
      useSharding
    );
    this.config.onProgress?.('D', 0, 'Starting sustained transfer test...');

    const { durationMs, concurrency } = TEST_CONFIG.testD;
    const metrics: ResourceTimingMetrics[] = [];
    const startTime = performance.now();
    let totalBytes = 0;
    let failedCount = 0;

    // Keep downloading until time runs out
    const activeDownloads = new Map<number, Promise<void>>();
    let downloadId = 0;

    const startDownload = (): void => {
      if (!this.isRunning) return;
      if (performance.now() - startTime >= durationMs) return;

      const currentId = downloadId++;
      const file = MEDIUM_FILES[currentId % MEDIUM_FILES.length];

      const downloadPromise = this.downloadWithTiming(file, useSharding)
        .then((metric) => {
          if (metric && this.isRunning) {
            metrics.push(metric);
            totalBytes += metric.fileSize;
            const elapsed = performance.now() - startTime;
            const progress = Math.min(100, (elapsed / durationMs) * 100);
            this.config.onProgress?.(
              'D',
              progress,
              `Sustained: ${(totalBytes / 1_000_000).toFixed(0)}MB downloaded`
            );
          } else if (!metric) {
            failedCount++;
          }
          activeDownloads.delete(currentId);
          // Start another download if time remains
          if (this.isRunning && performance.now() - startTime < durationMs) {
            startDownload();
          }
        })
        .catch(() => {
          failedCount++;
          activeDownloads.delete(currentId);
        });

      activeDownloads.set(currentId, downloadPromise);
    };

    // Start initial concurrent downloads
    for (
      let connectionIndex = 0;
      connectionIndex < concurrency;
      connectionIndex++
    ) {
      startDownload();
    }

    // Wait for test duration
    await new Promise<void>((resolve) => {
      const checkInterval = setInterval(() => {
        const elapsed = performance.now() - startTime;
        if (elapsed >= durationMs || !this.isRunning) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
    });

    // Wait for active downloads to finish
    await Promise.allSettled(Array.from(activeDownloads.values()));

    const endTime = performance.now();
    const result = this.buildTestResult(
      'D',
      'Sustained Transfer',
      `${(durationMs / 1000).toFixed(0)}s continuous download${useSharding ? ` across ${this.shardHostnames.length} hostnames` : ''}`,
      startTime,
      endTime,
      totalBytes,
      concurrency,
      metrics,
      failedCount
    );

    this.config.onTestComplete?.(result);
    return result;
  }

  /**
   * Normalize protocol string from PerformanceResourceTiming.nextHopProtocol
   * Common values: 'h2', 'h3', 'http/1.1', '' (unknown)
   */
  private normalizeProtocol(protocol: string): HttpProtocol {
    const normalized = protocol.toLowerCase().trim();
    if (normalized === 'h2' || normalized === 'http/2') {
      return 'h2';
    }
    if (normalized === 'h3' || normalized === 'http/3') {
      return 'h3';
    }
    if (normalized === 'http/1.1' || normalized === 'http/1.0') {
      return 'http/1.1';
    }
    return 'unknown';
  }

  /**
   * Download a file and extract Resource Timing metrics
   *
   * @param assetId - Asset identifier to download
   * @param useSharding - Whether to use domain sharding (distributes across hostnames)
   */
  private async downloadWithTiming(
    assetId: string,
    useSharding = false
  ): Promise<ResourceTimingMetrics | null> {
    // Get URL with optional sharding
    const { url, hostname } = this.getAssetUrl(assetId, useSharding);

    try {
      const fetchStart = performance.now();
      debugLog(
        `[Download] Fetching ${assetId} from ${hostname}... (proxy: ${this.isProxyMode()})`
      );

      const response = await fetch(url, {
        cache: 'no-store',
        signal: this.abortController?.signal,
      });

      debugLog(
        `[Download] Response for ${assetId} from ${hostname}: status=${response.status}, ok=${response.ok}`
      );

      if (!response.ok || !response.body) {
        console.warn(
          `[Download] Failed ${assetId} from ${hostname}: no body or not ok`
        );
        return null;
      }

      // Read the full response to get accurate timing
      const reader = response.body.getReader();
      let fileSize = 0;
      let lastLoggedMB = 0;
      const { progressLogIntervalMB } = TEST_CONFIG.logging;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        fileSize += value.length;
        // Log every N MB to reduce log spam for large files
        const currentMB = Math.floor(
          fileSize / (progressLogIntervalMB * 1024 * 1024)
        );
        if (currentMB > lastLoggedMB) {
          lastLoggedMB = currentMB;
          debugLog(
            `[Download] ${assetId} from ${hostname}: ${(fileSize / 1024 / 1024).toFixed(0)}MB received`
          );
        }
      }
      debugLog(
        `[Download] ${assetId} from ${hostname}: completed ${(fileSize / 1024 / 1024).toFixed(1)}MB`
      );

      const fetchEnd = performance.now();
      const totalDuration = fetchEnd - fetchStart;

      // Try to get Resource Timing data
      const entries = performance.getEntriesByName(
        url
      ) as PerformanceResourceTiming[];
      const timing = entries[entries.length - 1]; // Get most recent

      // Initialize with defaults
      let dnsTime = 0;
      let tcpConnectTime = 0;
      let tlsHandshakeTime = 0;
      let ttfb = 0;
      let downloadDuration = totalDuration;
      let nextHopProtocol: HttpProtocol = 'unknown';
      let transferSize = 0;
      let encodedBodySize = 0;
      let decodedBodySize = 0;

      if (timing) {
        // DNS, TCP, TLS timing (may be 0 due to connection reuse - that's normal)
        dnsTime = timing.domainLookupEnd - timing.domainLookupStart;
        tcpConnectTime = timing.connectEnd - timing.connectStart;
        tlsHandshakeTime =
          timing.secureConnectionStart > 0
            ? timing.connectEnd - timing.secureConnectionStart
            : 0;
        ttfb = timing.responseStart - timing.requestStart;
        downloadDuration = timing.responseEnd - timing.responseStart;

        // Protocol detection (critical for HTTP/2 and HTTP/3 awareness)
        nextHopProtocol = this.normalizeProtocol(timing.nextHopProtocol || '');

        // Cache detection from transfer size fields
        // transferSize = 0 when served from cache (but decodedBodySize > 0)
        transferSize = timing.transferSize || 0;
        encodedBodySize = timing.encodedBodySize || 0;
        decodedBodySize = timing.decodedBodySize || 0;

        // Clean up performance entries to avoid memory buildup
        performance.clearResourceTimings();
      }

      // Cache heuristic: transferSize is 0 but we got content
      const servedFromCache = transferSize === 0 && decodedBodySize > 0;

      const throughputMbps =
        downloadDuration > 0
          ? (fileSize * 8) / (downloadDuration / 1000) / 1_000_000
          : 0;

      return {
        transactionId: crypto.randomUUID(),
        url: assetId,
        requestUrl: url,
        fileSize,
        dnsTime: Math.max(0, dnsTime),
        tcpConnectTime: Math.max(0, tcpConnectTime),
        tlsHandshakeTime: Math.max(0, tlsHandshakeTime),
        ttfb: Math.max(0, ttfb),
        downloadDuration: Math.max(0, downloadDuration),
        totalDuration,
        throughputMbps,
        nextHopProtocol,
        hostname,
        transferSize,
        encodedBodySize,
        decodedBodySize,
        servedFromCache,
      };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return null;
      }
      console.error(`[DiagnosticTest] Failed to download ${assetId}:`, error);
      return null;
    }
  }

  /**
   * Build test result with aggregated metrics
   *
   * Note: goodputOverallMbps is computed identically here and in aggregateMetrics
   * to ensure consistency. Both use: totalBytes * 8 / (totalDurationMs / 1000) / 1_000_000
   */
  private buildTestResult(
    testId: 'A' | 'B' | 'C' | 'D',
    testName: string,
    description: string,
    startTime: number,
    endTime: number,
    totalBytes: number,
    concurrency: number,
    metrics: ResourceTimingMetrics[],
    failedCount: number
  ): TestResult {
    const totalDurationMs = endTime - startTime;
    // Goodput (overall) = total bytes transferred / wall-clock time
    // This is THE headline metric and must align with displayed bytes and duration
    const goodputOverallMbps =
      totalDurationMs > 0
        ? (totalBytes * 8) / (totalDurationMs / 1000) / 1_000_000
        : 0;

    // Warn if significant failures occurred
    if (failedCount > 0) {
      const totalAttempts = metrics.length + failedCount;
      const failureRate = (failedCount / totalAttempts) * 100;
      console.warn(
        `[${testId}] ${failedCount}/${totalAttempts} requests failed (${failureRate.toFixed(1)}%)`
      );
    }

    return {
      testId,
      testName,
      description,
      startTime,
      endTime,
      totalDurationMs,
      totalBytes,
      goodputOverallMbps: Math.round(goodputOverallMbps * 10) / 10, // Round to 1 decimal
      fileCount: metrics.length,
      failedCount,
      concurrency,
      metrics,
      aggregated: this.aggregateMetrics(metrics, totalDurationMs),
    };
  }

  /**
   * Aggregate protocol usage from metrics
   */
  private aggregateProtocols(
    metrics: ResourceTimingMetrics[]
  ): ProtocolAggregation {
    let h2Count = 0;
    let h3Count = 0;
    let http11Count = 0;
    let unknownCount = 0;

    for (const metric of metrics) {
      switch (metric.nextHopProtocol) {
        case 'h2':
          h2Count++;
          break;
        case 'h3':
          h3Count++;
          break;
        case 'http/1.1':
          http11Count++;
          break;
        default:
          unknownCount++;
      }
    }

    const totalCount = metrics.length;

    // Determine dominant protocol
    const counts = [
      { protocol: 'h2' as HttpProtocol, count: h2Count },
      { protocol: 'h3' as HttpProtocol, count: h3Count },
      { protocol: 'http/1.1' as HttpProtocol, count: http11Count },
      { protocol: 'unknown' as HttpProtocol, count: unknownCount },
    ];
    const dominantProtocol = counts.reduce((maxProto, current) =>
      current.count > maxProto.count ? current : maxProto
    ).protocol;

    // Multiplexed if dominant protocol is h2 or h3
    const isMultiplexed =
      dominantProtocol === 'h2' || dominantProtocol === 'h3';

    return {
      h2Count,
      h3Count,
      http11Count,
      unknownCount,
      totalCount,
      dominantProtocol,
      isMultiplexed,
    };
  }

  /**
   * Aggregate hostname distribution from metrics
   */
  private aggregateHostnames(
    metrics: ResourceTimingMetrics[]
  ): HostnameAggregation {
    const requestsPerHostname: Record<string, number> = {};

    for (const metric of metrics) {
      const hostname = metric.hostname || 'unknown';
      requestsPerHostname[hostname] = (requestsPerHostname[hostname] || 0) + 1;
    }

    const hostnames = Object.keys(requestsPerHostname);
    const hostnameCount = hostnames.length;

    // Check if requests are evenly distributed (within 20% of average)
    let evenlyDistributed = false;
    if (hostnameCount > 1) {
      const avgPerHost = metrics.length / hostnameCount;
      const threshold = avgPerHost * 0.2;
      evenlyDistributed = hostnames.every(
        (hostname) =>
          Math.abs(requestsPerHostname[hostname] - avgPerHost) <= threshold
      );
    }

    return {
      hostnameCount,
      requestsPerHostname,
      evenlyDistributed,
    };
  }

  /**
   * Calculate aggregated metrics from individual file metrics
   * Discards the first measurement to remove cold-connection outliers
   *
   * Key metric definitions:
   * - goodputOverallMbps: totalBytesTransferred * 8 / totalElapsedWallClockSeconds / 1e6
   *   This is THE headline metric and must be mathematically consistent with displayed bytes/duration
   * - Per-file throughput: Distribution of individual file download speeds (p10, p50)
   * - TTFB: Latency distribution (p10, p50, p95)
   * - Connection timings: Median of observable requests only, with observability % tracking
   *
   * @param metrics - Individual request metrics
   * @param totalDurationMs - Total wall-clock time for the test (for goodput calculation)
   */
  private aggregateMetrics(
    metrics: ResourceTimingMetrics[],
    totalDurationMs: number
  ): AggregatedMetrics {
    // Empty defaults with proper structure
    const emptyProtocolAggregation: ProtocolAggregation = {
      h2Count: 0,
      h3Count: 0,
      http11Count: 0,
      unknownCount: 0,
      totalCount: 0,
      dominantProtocol: 'unknown',
      isMultiplexed: false,
    };

    const emptyHostnameAggregation: HostnameAggregation = {
      hostnameCount: 0,
      requestsPerHostname: {},
      evenlyDistributed: false,
    };

    if (metrics.length === 0) {
      return {
        dnsObservablePercent: 0,
        tcpObservablePercent: 0,
        tlsObservablePercent: 0,
        medianDnsTime: 0,
        medianTcpConnectTime: 0,
        medianTlsHandshakeTime: 0,
        p10Ttfb: 0,
        p50Ttfb: 0,
        p95Ttfb: 0,
        minTtfb: 0,
        maxTtfb: 0,
        avgDownloadDuration: 0,
        p10ThroughputMbps: 0,
        p50ThroughputMbps: 0,
        minThroughputMbps: 0,
        maxThroughputMbps: 0,
        goodputOverallMbps: 0,
        protocolAggregation: emptyProtocolAggregation,
        hostnameAggregation: emptyHostnameAggregation,
        cachedRequestCount: 0,
        uncachedRequestCount: 0,
      };
    }

    // Skip the first measurement (cold-connection outlier)
    // Only skip if we have enough samples to make it meaningful
    const metricsForAggregation =
      metrics.length > 3 ? metrics.slice(1) : metrics;

    // Utility functions
    const sum = (arr: number[]) => arr.reduce((acc, val) => acc + val, 0);
    const avg = (arr: number[]) => (arr.length > 0 ? sum(arr) / arr.length : 0);
    const percentile = (arr: number[], pct: number) => {
      if (arr.length === 0) return 0;
      const sorted = [...arr].sort((first, second) => first - second);
      const index = Math.floor((pct / 100) * sorted.length);
      return sorted[Math.min(index, sorted.length - 1)];
    };
    const roundOne = (value: number) => Math.round(value * 10) / 10;
    const roundPercent = (value: number) => Math.round(value);

    // Connection timing observability analysis
    // Track what % of requests had non-zero (observable) timings
    const OBSERVABILITY_THRESHOLD = 0.2; // 20% - below this, mark as "not observable"

    const dnsTimings = metricsForAggregation.map(
      (measurement) => measurement.dnsTime
    );
    const tcpTimings = metricsForAggregation.map(
      (measurement) => measurement.tcpConnectTime
    );
    const tlsTimings = metricsForAggregation.map(
      (measurement) => measurement.tlsHandshakeTime
    );

    const nonZeroDns = dnsTimings.filter((timing) => timing > 0);
    const nonZeroTcp = tcpTimings.filter((timing) => timing > 0);
    const nonZeroTls = tlsTimings.filter((timing) => timing > 0);

    const totalRequests = metricsForAggregation.length;
    const dnsObservablePercent =
      totalRequests > 0 ? (nonZeroDns.length / totalRequests) * 100 : 0;
    const tcpObservablePercent =
      totalRequests > 0 ? (nonZeroTcp.length / totalRequests) * 100 : 0;
    const tlsObservablePercent =
      totalRequests > 0 ? (nonZeroTls.length / totalRequests) * 100 : 0;

    // Only compute median if observability >= 20%, otherwise return 0
    // This prevents presenting "0ms" as a meaningful measurement
    const medianDnsTime =
      dnsObservablePercent >= OBSERVABILITY_THRESHOLD * 100
        ? roundOne(percentile(nonZeroDns, 50))
        : 0;
    const medianTcpConnectTime =
      tcpObservablePercent >= OBSERVABILITY_THRESHOLD * 100
        ? roundOne(percentile(nonZeroTcp, 50))
        : 0;
    const medianTlsHandshakeTime =
      tlsObservablePercent >= OBSERVABILITY_THRESHOLD * 100
        ? roundOne(percentile(nonZeroTls, 50))
        : 0;

    // Extract arrays for statistics
    const throughputs = metricsForAggregation.map(
      (measurement) => measurement.throughputMbps
    );
    const ttfbs = metricsForAggregation.map((measurement) => measurement.ttfb);

    // Calculate goodput (overall): total bytes / wall clock time
    // This is THE headline metric - must align mathematically with displayed bytes and duration
    const totalBytes = sum(metrics.map((measurement) => measurement.fileSize));
    const goodputOverallMbps =
      totalDurationMs > 0
        ? (totalBytes * 8) / (totalDurationMs / 1000) / 1_000_000
        : 0;

    // Aggregate protocols and hostnames (use ALL metrics, not just filtered)
    const protocolAggregation = this.aggregateProtocols(metrics);
    const hostnameAggregation = this.aggregateHostnames(metrics);

    // Count cached vs uncached requests
    const cachedRequestCount = metrics.filter(
      (measurement) => measurement.servedFromCache
    ).length;
    const uncachedRequestCount = metrics.length - cachedRequestCount;

    return {
      // Connection timing observability
      dnsObservablePercent: roundPercent(dnsObservablePercent),
      tcpObservablePercent: roundPercent(tcpObservablePercent),
      tlsObservablePercent: roundPercent(tlsObservablePercent),

      // Connection timings (median of observable only)
      medianDnsTime,
      medianTcpConnectTime,
      medianTlsHandshakeTime,

      // TTFB distribution: p10 (fast), p50 (typical), p95 (tail latency)
      p10Ttfb: roundOne(percentile(ttfbs, 10)),
      p50Ttfb: roundOne(percentile(ttfbs, 50)),
      p95Ttfb: roundOne(percentile(ttfbs, 95)),
      minTtfb: roundOne(ttfbs.length > 0 ? Math.min(...ttfbs) : 0),
      maxTtfb: roundOne(ttfbs.length > 0 ? Math.max(...ttfbs) : 0),

      // Download duration (average is acceptable here)
      avgDownloadDuration: roundOne(
        avg(
          metricsForAggregation.map(
            (measurement) => measurement.downloadDuration
          )
        )
      ),

      // Per-file throughput distribution: p10 (slow path), p50 (typical)
      p10ThroughputMbps: roundOne(percentile(throughputs, 10)),
      p50ThroughputMbps: roundOne(percentile(throughputs, 50)),
      minThroughputMbps: roundOne(
        throughputs.length > 0 ? Math.min(...throughputs) : 0
      ),
      maxThroughputMbps: roundOne(
        throughputs.length > 0 ? Math.max(...throughputs) : 0
      ),

      // Goodput (overall) - THE headline metric
      goodputOverallMbps: roundOne(goodputOverallMbps),

      protocolAggregation,
      hostnameAggregation,
      cachedRequestCount,
      uncachedRequestCount,
    };
  }

  /**
   * Determine connection forcing confidence based on protocol and hostname distribution
   */
  private determineConnectionForcingConfidence(
    protocolAggregation: ProtocolAggregation,
    hostnameAggregation: HostnameAggregation
  ): ConnectionForcingConfidence {
    const { dominantProtocol, isMultiplexed } = protocolAggregation;
    const { hostnameCount, evenlyDistributed } = hostnameAggregation;

    // High confidence: HTTP/1.1 with multiple hostnames, or multiple hostnames evenly distributed
    if (dominantProtocol === 'http/1.1' && hostnameCount > 1) {
      return 'high';
    }

    // Medium confidence: Multiple hostnames but multiplexed protocol (h2/h3)
    // Connection forcing may still work across different hostnames
    if (hostnameCount > 1 && evenlyDistributed && !isMultiplexed) {
      return 'high';
    }

    if (hostnameCount > 1 && evenlyDistributed) {
      return 'medium';
    }

    // Low confidence: Single hostname or protocol unknown
    return 'low';
  }

  /**
   * Analyze results to detect network issues
   * Uses protocol-aware heuristics and confidence levels
   */
  private analyzeResults(
    testA: TestResult,
    testB: TestResult,
    testC: TestResult,
    testD: TestResult
  ): DiagnosticAnalysis {
    const issues: string[] = [];
    const recommendations: string[] = [];

    // Extract protocol and hostname info from Test B (the key parallel test)
    const testBProtocol = testB.aggregated.protocolAggregation;
    const testBHostnames = testB.aggregated.hostnameAggregation;
    const connectionForcingConfidence =
      this.determineConnectionForcingConfidence(testBProtocol, testBHostnames);

    // =========================================================================
    // Connection Limit Analysis (Test B vs Test A)
    // Uses goodput instead of per-file throughput for more accurate comparison
    // =========================================================================
    const baselineGoodputMbps = testA.aggregated.goodputOverallMbps;
    const parallelGoodputMbps = testB.aggregated.goodputOverallMbps;
    const goodputRatio =
      baselineGoodputMbps > 0 ? parallelGoodputMbps / baselineGoodputMbps : 1;

    // Connection limits: parallel should not be significantly worse than sequential
    // Threshold depends on confidence level
    let connectionLimitThreshold = 0.7;
    if (connectionForcingConfidence === 'low') {
      // With low confidence (multiplexed, single hostname), be more lenient
      // because apparent slowdown may be due to multiplexing overhead
      connectionLimitThreshold = 0.5;
    }

    const connectionLimitDetected = goodputRatio < connectionLimitThreshold;

    let connectionLimitReason = '';
    if (connectionLimitDetected) {
      const degradation = Math.round((1 - goodputRatio) * 100);
      connectionLimitReason = `Parallel downloads achieved ${degradation}% lower goodput than sequential baseline`;

      if (connectionForcingConfidence === 'high') {
        connectionLimitReason +=
          '. High confidence: multiple connections forced via hostname sharding.';
        issues.push(
          `Connection limit likely: ${degradation}% goodput degradation with parallel downloads`
        );
        recommendations.push(
          'Your network may have connection limits (proxy/VPN). Try disabling VPN or using a different network.'
        );
      }

      if (connectionForcingConfidence === 'medium') {
        connectionLimitReason += `. Medium confidence: ${testBProtocol.dominantProtocol} detected (may be multiplexed).`;
        issues.push(
          `Possible connection limit: ${degradation}% goodput degradation (${testBProtocol.dominantProtocol} protocol detected)`
        );
        recommendations.push(
          'Parallel downloads show degradation. This may indicate connection limits, but HTTP/2 multiplexing makes this uncertain.'
        );
      }

      if (connectionForcingConfidence === 'low') {
        connectionLimitReason +=
          '. Low confidence: single hostname or protocol not detected reliably.';
        // Don't add to issues for low confidence
        recommendations.push(
          'Parallel downloads showed some degradation, but confidence is low due to protocol multiplexing.'
        );
      }
    }

    if (!connectionLimitDetected) {
      connectionLimitReason =
        'Parallel goodput is within acceptable range of baseline';
    }

    const connectionLimit: ConnectionLimitAnalysis = {
      detected: connectionLimitDetected,
      confidence: connectionForcingConfidence,
      baselineGoodputMbps,
      parallelGoodputMbps,
      goodputRatio: Math.round(goodputRatio * 100) / 100,
      hostnamesUsed: testBHostnames.hostnameCount,
      dominantProtocol: testBProtocol.dominantProtocol,
      reason: connectionLimitReason,
    };

    // =========================================================================
    // Inspection Overhead Analysis (Test C TTFB percentiles)
    // Uses p50 and p95 TTFB to detect per-request inspection overhead
    // =========================================================================
    const baselineTtfbP50 = testA.aggregated.p50Ttfb;
    const parallelTtfbP50 = testB.aggregated.p50Ttfb;
    const smallFileTtfbP50 = testC.aggregated.p50Ttfb;
    const smallFileTtfbP95 = testC.aggregated.p95Ttfb;

    // Tail latency ratio: p95/p50 indicates variance
    // High ratio suggests inconsistent latency (inspection, queueing, etc.)
    const ttfbTailLatencyRatio =
      smallFileTtfbP50 > 0 ? smallFileTtfbP95 / smallFileTtfbP50 : 1;

    // Compare Test C to Test B (both have parallelism)
    // Significant p50 increase + high tail latency suggests inspection
    const ttfbVsParallelP50 =
      parallelTtfbP50 > 0 ? smallFileTtfbP50 / parallelTtfbP50 : 1;
    const ttfbVsBaselineP50 =
      baselineTtfbP50 > 0 ? smallFileTtfbP50 / baselineTtfbP50 : 1;

    // Inspection overhead heuristics:
    // 1. Test C p50 TTFB significantly higher than Test B p50 (>50%) AND high tail latency (>2.5x)
    // 2. OR Test C p50 is >3x the baseline p50
    // 3. OR extreme tail latency ratio (>10x) - this alone is abnormal
    // 4. OR p95 TTFB > 5 seconds - this indicates severe issues
    const significantTtfbIncrease = ttfbVsParallelP50 > 1.5;
    const highTailLatency = ttfbTailLatencyRatio > 2.5;
    const extremeTtfbIncrease = ttfbVsBaselineP50 > 3.0;
    const veryHighTailLatency = ttfbTailLatencyRatio > 10;
    const extremeP95 = smallFileTtfbP95 > 5000; // 5+ seconds

    const inspectionOverheadDetected =
      (significantTtfbIncrease && highTailLatency) ||
      extremeTtfbIncrease ||
      veryHighTailLatency ||
      extremeP95;

    let inspectionReason = '';
    if (inspectionOverheadDetected) {
      inspectionReason = `Small file TTFB p50=${smallFileTtfbP50.toFixed(0)}ms, p95=${smallFileTtfbP95.toFixed(0)}ms. `;
      inspectionReason += `Tail latency ratio: ${ttfbTailLatencyRatio.toFixed(1)}x. `;

      if (extremeP95) {
        inspectionReason +=
          'CRITICAL: p95 TTFB > 5 seconds indicates severe network issues. ';
        issues.push(
          `Extreme latency detected: p95 TTFB is ${(smallFileTtfbP95 / 1000).toFixed(1)} seconds`
        );
      } else if (veryHighTailLatency) {
        inspectionReason += `Very high tail latency (${ttfbTailLatencyRatio.toFixed(0)}x) indicates inconsistent request handling. `;
        issues.push(
          `High per-request latency: small file TTFB p95 (${smallFileTtfbP95.toFixed(0)}ms) is ${ttfbTailLatencyRatio.toFixed(1)}x the p50 (${smallFileTtfbP50.toFixed(0)}ms)`
        );
      } else {
        inspectionReason += `vs parallel p50: ${ttfbVsParallelP50.toFixed(1)}x, vs baseline: ${ttfbVsBaselineP50.toFixed(1)}x.`;
        issues.push(
          `High per-request latency: small file TTFB p95 (${smallFileTtfbP95.toFixed(0)}ms) is ${ttfbTailLatencyRatio.toFixed(1)}x the p50 (${smallFileTtfbP50.toFixed(0)}ms)`
        );
      }

      recommendations.push(
        'Your network may be inspecting traffic (corporate firewall, antivirus) or experiencing congestion. The high tail latency suggests per-request processing overhead or queueing.'
      );
    }

    if (!inspectionOverheadDetected) {
      inspectionReason = 'TTFB percentiles are within acceptable range';
    }

    const inspectionOverhead: InspectionOverheadAnalysis = {
      detected: inspectionOverheadDetected,
      confidence: connectionForcingConfidence,
      baselineTtfbP50,
      parallelTtfbP50,
      smallFileTtfbP50,
      smallFileTtfbP95,
      ttfbTailLatencyRatio: Math.round(ttfbTailLatencyRatio * 10) / 10,
      reason: inspectionReason,
    };

    // Note about small file throughput - it's naturally lower (more overhead per byte)
    // Test C throughput is NOT a capacity metric - it measures latency overhead
    const smallFileRatio =
      testA.goodputOverallMbps > 0
        ? testC.goodputOverallMbps / testA.goodputOverallMbps
        : 1;
    if (smallFileRatio < 0.3 && !inspectionOverheadDetected) {
      recommendations.push(
        `Small files (${testC.goodputOverallMbps.toFixed(0)} Mbps) are slower than large files (${testA.goodputOverallMbps.toFixed(0)} Mbps) - this is normal due to HTTP overhead per request.`
      );
    }

    // =========================================================================
    // Throttling Analysis (Test D throughput over time)
    // Uses duration-aware windowing to detect ISP throttling
    //
    // Window sizes: min(10s, 25% of total duration)
    // - Ramp-up window: samples from the beginning of the test
    // - Steady-state window: samples from the end of the test
    //
    // This approach handles variable test durations appropriately:
    // - 30s test: 7.5s windows (25%)
    // - 60s test: 10s windows (capped at 10s)
    // =========================================================================
    const sustainedMetrics = testD.metrics;
    const testDDurationMs = testD.totalDurationMs;
    let throttlingDetected = false;
    let rampUpThroughputMbps = 0;
    let steadyStateThroughputMbps = 0;
    let rampUpWindowMs = 0;
    let steadyStateWindowMs = 0;
    let stableWindowPct = 0;
    // Legacy fields (backward compatibility)
    let firstHalfThroughputMbps = 0;
    let secondHalfThroughputMbps = 0;
    let degradationRatio = 1;
    let throttlingReason = 'Insufficient data for throttling analysis';

    if (sustainedMetrics.length >= 4 && testDDurationMs > 0) {
      // Calculate window size: min(10 seconds, 25% of total duration)
      const WINDOW_CAP_MS = 10000; // 10 seconds max
      const WINDOW_PERCENT = 0.25; // 25% of duration
      const windowSizeMs = Math.min(
        WINDOW_CAP_MS,
        testDDurationMs * WINDOW_PERCENT
      );

      rampUpWindowMs = windowSizeMs;
      steadyStateWindowMs = windowSizeMs;

      // Find the start time of the test (first metric's start)
      // Metrics don't have absolute timestamps, so we use relative positioning
      // based on cumulative duration from start
      let cumulativeTime = 0;
      const metricsWithTimestamp: Array<{
        metric: ResourceTimingMetrics;
        startTimeMs: number;
        endTimeMs: number;
      }> = [];

      for (const metric of sustainedMetrics) {
        metricsWithTimestamp.push({
          metric,
          startTimeMs: cumulativeTime,
          endTimeMs: cumulativeTime + metric.totalDuration,
        });
        // Note: For parallel downloads, this is approximate
        // but provides reasonable windowing behavior
        cumulativeTime += metric.totalDuration / 4; // Approximate overlap
      }

      // Get ramp-up window: metrics that started within first windowSizeMs
      const rampUpMetrics = metricsWithTimestamp
        .filter((entry) => entry.startTimeMs < windowSizeMs)
        .map((entry) => entry.metric);

      // Get steady-state window: metrics that ended within last windowSizeMs
      const totalApproxDuration = cumulativeTime;
      const steadyStateStartMs = Math.max(
        0,
        totalApproxDuration - windowSizeMs
      );
      const steadyStateMetrics = metricsWithTimestamp
        .filter((entry) => entry.endTimeMs > steadyStateStartMs)
        .map((entry) => entry.metric);

      // Calculate throughput for each window
      const calculateWindowThroughput = (
        metrics: ResourceTimingMetrics[]
      ): number => {
        if (metrics.length === 0) return 0;
        const totalBytes = metrics.reduce(
          (sum, metric) => sum + metric.fileSize,
          0
        );
        const totalDuration = metrics.reduce(
          (sum, metric) => sum + metric.totalDuration,
          0
        );
        if (totalDuration <= 0) return 0;
        return (totalBytes * 8) / (totalDuration / 1000) / 1_000_000;
      };

      rampUpThroughputMbps = calculateWindowThroughput(rampUpMetrics);
      steadyStateThroughputMbps = calculateWindowThroughput(steadyStateMetrics);

      // Calculate stable window percentage (middle portion)
      // stableWindowPct = 100 - (rampUp% + steadyState%)
      const rampUpPct = (windowSizeMs / testDDurationMs) * 100;
      const steadyStatePct = (windowSizeMs / testDDurationMs) * 100;
      stableWindowPct = Math.max(0, 100 - rampUpPct - steadyStatePct);

      // Calculate degradation ratio using windowed metrics
      degradationRatio =
        rampUpThroughputMbps > 0
          ? steadyStateThroughputMbps / rampUpThroughputMbps
          : 1;

      // Legacy first/second half calculation (for backward compatibility)
      const midpoint = Math.floor(sustainedMetrics.length / 2);
      const firstHalf = sustainedMetrics.slice(0, midpoint);
      const secondHalf = sustainedMetrics.slice(midpoint);

      const firstHalfBytes = firstHalf.reduce(
        (sum, metric) => sum + metric.fileSize,
        0
      );
      const firstHalfDuration = firstHalf.reduce(
        (sum, metric) => sum + metric.totalDuration,
        0
      );
      const secondHalfBytes = secondHalf.reduce(
        (sum, metric) => sum + metric.fileSize,
        0
      );
      const secondHalfDuration = secondHalf.reduce(
        (sum, metric) => sum + metric.totalDuration,
        0
      );

      firstHalfThroughputMbps =
        firstHalfDuration > 0
          ? (firstHalfBytes * 8) / (firstHalfDuration / 1000) / 1_000_000
          : 0;
      secondHalfThroughputMbps =
        secondHalfDuration > 0
          ? (secondHalfBytes * 8) / (secondHalfDuration / 1000) / 1_000_000
          : 0;

      // Detect throttling: significant drop from ramp-up to steady-state
      throttlingDetected = degradationRatio < 0.7;

      if (throttlingDetected) {
        const degradationPercent = Math.round((1 - degradationRatio) * 100);
        throttlingReason = `Speed dropped ${degradationPercent}% from ramp-up (${rampUpThroughputMbps.toFixed(0)} Mbps) to steady-state (${steadyStateThroughputMbps.toFixed(0)} Mbps). `;
        throttlingReason += `Window size: ${(windowSizeMs / 1000).toFixed(1)}s (${Math.round((windowSizeMs / testDDurationMs) * 100)}% of test)`;
        issues.push(
          `Throttling detected: ${degradationPercent}% speed drop during sustained transfer`
        );
        recommendations.push(
          'Your ISP may be throttling sustained downloads. Speed was higher at the start than the end.'
        );
      }

      if (!throttlingDetected) {
        throttlingReason = `Sustained transfer maintained consistent throughput (${degradationRatio.toFixed(2)}x ramp-up to steady-state ratio). `;
        throttlingReason += `Window size: ${(windowSizeMs / 1000).toFixed(1)}s`;
      }
    }

    const throttling: ThrottlingAnalysis = {
      detected: throttlingDetected,
      confidence: connectionForcingConfidence,
      // Duration-aware windowing metrics
      rampUpThroughputMbps: Math.round(rampUpThroughputMbps * 10) / 10,
      steadyStateThroughputMbps:
        Math.round(steadyStateThroughputMbps * 10) / 10,
      rampUpWindowMs: Math.round(rampUpWindowMs),
      steadyStateWindowMs: Math.round(steadyStateWindowMs),
      stableWindowPct: Math.round(stableWindowPct),
      // Legacy fields (backward compatibility)
      firstHalfThroughputMbps: Math.round(firstHalfThroughputMbps * 10) / 10,
      secondHalfThroughputMbps: Math.round(secondHalfThroughputMbps * 10) / 10,
      degradationRatio: Math.round(degradationRatio * 100) / 100,
      reason: throttlingReason,
    };

    // =========================================================================
    // Overall Summary
    // =========================================================================
    const protocolSummary = this.buildProtocolSummary(testBProtocol);

    // Overall confidence is the minimum of individual analysis confidences
    let overallConfidence: ConnectionForcingConfidence = 'high';
    if (connectionForcingConfidence === 'low') {
      overallConfidence = 'low';
    }
    if (connectionForcingConfidence === 'medium') {
      overallConfidence = 'medium';
    }

    return {
      connectionLimit,
      inspectionOverhead,
      throttling,
      connectionLimitDetected,
      inspectionOverheadDetected,
      throttlingDetected,
      issues,
      recommendations,
      overallConfidence,
      protocolSummary,
    };
  }

  /**
   * Build human-readable protocol summary
   */
  private buildProtocolSummary(
    protocolAggregation: ProtocolAggregation
  ): string {
    const {
      dominantProtocol,
      isMultiplexed,
      h2Count,
      h3Count,
      http11Count,
      totalCount,
    } = protocolAggregation;

    if (totalCount === 0) {
      return 'No protocol data available';
    }

    const protocolName =
      dominantProtocol === 'h2'
        ? 'HTTP/2'
        : dominantProtocol === 'h3'
          ? 'HTTP/3'
          : dominantProtocol === 'http/1.1'
            ? 'HTTP/1.1'
            : 'Unknown';

    let summary = `${protocolName} detected`;
    if (isMultiplexed) {
      summary += ' (multiplexed - streams share connections)';
    }

    // Add breakdown if mixed protocols
    const mixedProtocols =
      (h2Count > 0 ? 1 : 0) +
        (h3Count > 0 ? 1 : 0) +
        (http11Count > 0 ? 1 : 0) >
      1;
    if (mixedProtocols) {
      const parts = [];
      if (h2Count > 0) parts.push(`h2: ${h2Count}`);
      if (h3Count > 0) parts.push(`h3: ${h3Count}`);
      if (http11Count > 0) parts.push(`http/1.1: ${http11Count}`);
      summary += ` [${parts.join(', ')}]`;
    }

    return summary;
  }

  /**
   * Abort all running tests
   */
  abort(): void {
    this.isRunning = false;
    this.abortController?.abort();
  }

  /**
   * Format bytes for display
   */
  static formatBytes(bytes: number): string {
    if (bytes >= 1_000_000_000) {
      return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
    }
    if (bytes >= 1_000_000) {
      return `${(bytes / 1_000_000).toFixed(0)} MB`;
    }
    if (bytes >= 1000) {
      return `${(bytes / 1000).toFixed(0)} KB`;
    }
    return `${bytes} B`;
  }

  /**
   * Format duration for display
   */
  static formatDuration(ms: number): string {
    if (ms >= 60000) {
      return `${(ms / 60000).toFixed(1)} min`;
    }
    if (ms >= 1000) {
      return `${(ms / 1000).toFixed(1)}s`;
    }
    return `${Math.round(ms)}ms`;
  }
}
